import React, { useState, useMemo, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import PropTypes from "prop-types";

import { DateRangePicker } from "react-dates";
import { Row, Col } from "react-bootstrap";
import moment from "moment";
import { orderBy } from "lodash";

import DropdownSearch, {
  DropdownSearchSelect
} from "../../common/DropdownSearch";

import { formatDate } from "../../../helpers/date";

import * as departmentActions from "../../../actions/departmentActions";
import * as customerActions from "../../../actions/customerActions";
import * as projectActions from "../../../actions/projectActions";
import * as reportActions from "../../../actions/reportActions";
import * as userActions from "../../../actions/userActions";
import * as codeActions from "../../../actions/codeActions";

import ReportFilters from "../ReportFiltersComponent";

const INITIAL_FILTERS_STATE = {
  department: [],
  customer: [],
  assigned_to: [],
  call_type: null,
  arrival_date: new Date(new Date().getFullYear(), 0, 1),
  completed_date: new Date()
};

const MonthlyRevenueAnalysisFilters = ({
  departments,
  customers,
  users,
  codes,
  actions,
  loading,
  onCSVExport,
  onPDFExport,
  exportDisabled
}) => {
  const [filters, setFilters] = useState(INITIAL_FILTERS_STATE);
  const [focusedInput, setFocusedInput] = useState(null);

  useEffect(() => {
    actions.loadDepartments();
    actions.loadCustomers();
    actions.loadUsers();
    actions.loadCodes();
  }, [actions]);

  useEffect(() => {
    if (codes.length) {
      const prepaid = codes.find(
        (code) =>
          code.entity === "Call" &&
          code.field === "type" &&
          code.label === "PREPAID"
      );
      if (prepaid) {
        setFilters((prev) => ({ ...prev, call_type: prepaid }));
      }
    }
  }, [codes]);

  const handleChangeDate = (startDate, endDate) => {
    setFilters((prev) => ({
      ...prev,
      ...(startDate && { arrival_date: new Date(startDate) }),
      ...(endDate && { completed_date: new Date(endDate) })
    }));
  };

  const handleChange = (name, data) => {
    setFilters((prev) => ({ ...prev, [name]: data }));
  };

  const handleResetFilters = () => {
    const newFilters = {
      customer: [],
      assigned_to: [],
      department: [],
      call_type: filters.call_type,
      arrival_date: new Date(new Date().getFullYear(), 0, 1),
      completed_date: new Date()
    };
    setFilters(newFilters);
    actions.cleanMRAReport();
  };

  const fetchData = useCallback(async () => {
    actions.getMonthlyRevenueAnalysisReport({
      filters
    });
  }, [actions, filters]);

  const departmentOptions = useMemo(() => {
    const options = departments.map((dep) => ({
      value: dep._id,
      label: dep.description
    }));
    return orderBy(options, [(option) => option.label.toLowerCase()]);
  }, [departments]);

  const customerOptions = useMemo(() => {
    const options = customers.map((customer) => ({
      value: customer._id,
      label: customer.name
    }));
    return orderBy(options, [(option) => option.label.toLowerCase()]);
  }, [customers]);

  const userOptions = useMemo(() => {
    const options = users.map((project) => ({
      value: project._id,
      label: project.full_name
    }));
    return orderBy(options, [(option) => option.label.toLowerCase()]);
  }, [users]);

  const codeOptions = useMemo(() => {
    const options = codes.map((code) => ({
      value: code._id,
      label: code.label
    }));
    return orderBy(options, [(option) => option.label.toLowerCase()]);
  }, [codes]);
  return (
    <ReportFilters
      onFetchClick={fetchData}
      onResetClick={handleResetFilters}
      title={`Monthly Revenue Analysis from ${formatDate(
        filters.arrival_date,
        "MM/dd/yyyy"
      )} to ${formatDate(filters.completed_date, "MM/dd/yyyy")}`}
      loading={loading}
      csvExportAvailable
      pdfExportAvailable
      onCSVExport={() => onCSVExport(filters)}
      onPDFExport={() => onPDFExport(filters)}
      exportDisabled={exportDisabled}
    >
      <Row style={{ marginBottom: 0 }}>
        <Col md={3}>
          <DropdownSearch
            isMulti
            label="Customer"
            placeholder="Select"
            name="customer"
            onChange={handleChange}
            value={filters.customer}
            data={customerOptions}
          />
        </Col>
        <Col md={3}>
          <DropdownSearch
            isClearable
            isMulti
            label="Department"
            placeholder="Select"
            name="department"
            onChange={handleChange}
            value={filters.department}
            data={departmentOptions}
          />
        </Col>
        <Col md={3}>
          <DropdownSearch
            isMulti
            label="Assigned To"
            placeholder="Select"
            name="assigned_to"
            onChange={handleChange}
            value={filters.assigned_to}
            data={userOptions}
          />
        </Col>
        <Col md={3}>
          <DropdownSearchSelect
            label
            fieldLabel="Call Type"
            placeholder="Select"
            name="call_type"
            onChange={handleChange}
            value={filters.call_type}
            data={codeOptions}
            disabled
          />
        </Col>
      </Row>
      <Row>
        <Col md={3}>
          <label
            className="control-label"
            style={{ textTransform: "uppercase" }}
          >
            Date Range
          </label>
          <DateRangePicker
            startDate={
              filters.arrival_date ? moment(filters.arrival_date) : null
            }
            startDateId="startDate"
            endDate={
              filters.completed_date ? moment(filters.completed_date) : null
            }
            endDateId="endDate"
            onDatesChange={({ startDate, endDate }) => {
              handleChangeDate(startDate, endDate);
            }}
            isOutsideRange={() => false}
            focusedInput={focusedInput}
            onFocusChange={setFocusedInput}
          />
        </Col>
      </Row>
    </ReportFilters>
  );
};

MonthlyRevenueAnalysisFilters.propTypes = {
  departments: PropTypes.array,
  customers: PropTypes.array,
  users: PropTypes.array,
  codes: PropTypes.array,
  actions: PropTypes.object,
  loading: PropTypes.bool,
  onCSVExport: PropTypes.func,
  onPDFExport: PropTypes.func,
  exportDisabled: PropTypes.bool
};

function mapStatesToProps(state) {
  return {
    departments: state.reducers.departments,
    customers: state.reducers.customers,
    users: state.reducers.users,
    codes: state.reducers.codes,
    loading: state.reducers.loading,
    auth: state.reducers.auth
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...departmentActions,
        ...customerActions,
        ...projectActions,
        ...reportActions,
        ...userActions,
        ...codeActions
      },
      dispatch
    )
  };
}

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(MonthlyRevenueAnalysisFilters);
